import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    id: Number,
    enabled: Boolean,
    frequency: String,
    day: Number,
    hour: Number,
  };

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    this.showModal();
  }

  showModal() {
    const modalHtml = this.buildModalHtml();
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById('notification-settings-modal');
    // Show modal by removing hidden class
    modal.classList.remove('tw-hidden');

    // Set up form submission
    const form = document.getElementById('notification-settings-form');
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Set up cancel button
    const cancelBtn = document.getElementById('notification-cancel-btn');
    cancelBtn.addEventListener('click', () => this.closeModal());

    // Set up backdrop click
    const backdrop = modal.querySelector('[data-modal-dismiss]');
    backdrop.addEventListener('click', () => this.closeModal());
  }

  closeModal() {
    const modal = document.getElementById('notification-settings-modal');
    if (modal) {
      modal.classList.add('tw-hidden');
      setTimeout(() => modal.remove(), 300);
    }
  }

  buildModalHtml() {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return `
      <div id="notification-settings-modal" class="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto tw-hidden">
        <div class="tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-px-4 tw-py-8">
          <div class="tw-fixed tw-inset-0 tw-bg-black tw-opacity-50" data-modal-dismiss></div>
          
          <div class="tw-relative tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-p-6">
            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">
              Email Notification Settings
            </h3>
            
            <form id="notification-settings-form" data-saved-search-id="${this.idValue}">
              <div class="tw-mb-4">
                <label class="tw-flex tw-items-center tw-cursor-pointer">
                  <input type="checkbox" 
                         name="email_notifications_enabled" 
                         class="tw-mr-2"
                         ${this.enabledValue ? 'checked' : ''}>
                  <span class="tw-text-sm tw-text-gray-700">Enable email notifications</span>
                </label>
              </div>
              
              <div id="notification-options" class="${this.enabledValue ? '' : 'tw-hidden'}">
                <div class="tw-mb-4">
                  <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Frequency
                  </label>
                  <select name="notification_frequency" class="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2">
                    <option value="daily" ${!this.frequencyValue || this.frequencyValue === 'daily' ? 'selected' : ''}>Daily</option>
                    <option value="weekly" ${this.frequencyValue === 'weekly' ? 'selected' : ''}>Weekly</option>
                  </select>
                </div>
                
                <div id="day-selector" class="tw-mb-4 ${this.frequencyValue === 'weekly' ? '' : 'tw-hidden'}">
                  <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Day of Week
                  </label>
                  <select name="notification_day_of_week" class="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2">
                    ${days
                      .map((day, index) => `<option value="${index}" ${(this.dayValue || 1) === index ? 'selected' : ''}>${day}</option>`)
                      .join('')}
                  </select>
                </div>
                
                <div class="tw-mb-4">
                  <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
                    Time of Day
                  </label>
                  <select name="notification_hour" class="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-2">
                    ${Array.from({ length: 24 }, (_, i) => {
                      const hour = i === 0 ? 12 : i > 12 ? i - 12 : i;
                      const ampm = i < 12 ? 'AM' : 'PM';
                      const display = i === 0 ? '12:00 AM' : `${hour}:00 ${ampm}`;
                      return `<option value="${i}" ${(this.hourValue || 9) === i ? 'selected' : ''}>${display}</option>`;
                    }).join('')}
                  </select>
                </div>
              </div>
              
              <div class="tw-flex tw-justify-end tw-space-x-3 tw-mt-6">
                <button type="button" 
                        id="notification-cancel-btn"
                        class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded hover:tw-bg-gray-50">
                  Cancel
                </button>
                <button type="submit" 
                        class="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded hover:tw-bg-blue-700">
                  Save Settings
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;
  }

  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const formData = new FormData(form);
    const isEnabling = formData.get('email_notifications_enabled') === 'on';
    // Default to 'daily' if no frequency is set (for new saved searches)
    const frequency = formData.get('notification_frequency') || this.frequencyValue || 'daily';

    const data = {
      saved_search: {
        email_notifications_enabled: isEnabling,
        notification_frequency: frequency,
        notification_day_of_week: formData.get('notification_day_of_week') || this.dayValue,
        notification_hour: formData.get('notification_hour') || this.hourValue,
      },
    };

    try {
      const response = await fetch(`/saved_searches/${this.idValue}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.success) {
        // Update the UI
        location.reload();
      } else {
        alert(result.message || 'An error occurred');
      }
    } catch (error) {
      alert('An error occurred while saving settings');
    }

    this.closeModal();
  }
}

// Set up event listeners for form interactions
document.addEventListener('change', (event) => {
  if (event.target.name === 'email_notifications_enabled') {
    const options = document.getElementById('notification-options');
    if (event.target.checked) {
      options.classList.remove('tw-hidden');
    } else {
      options.classList.add('tw-hidden');
    }
  }

  if (event.target.name === 'notification_frequency') {
    const daySelector = document.getElementById('day-selector');
    if (event.target.value === 'weekly') {
      daySelector.classList.remove('tw-hidden');
    } else {
      daySelector.classList.add('tw-hidden');
    }
  }
});
